import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarRange, Plus } from 'lucide-react';
import { WarningIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import SectionHeading from '../../../components/SectionHeading';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import { formatDate } from '../../lib/format';
import { CycleChips, type LegislativeBody, type LegislativeCycleRow } from './cycleTypes';

interface CyclesPayload {
  bodies: LegislativeBody[];
  cycles: LegislativeCycleRow[];
}

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';
const COLUMN_COUNT = 4;

const EMPTY_FORM = { body_id: '', number: '', label: '', start_date: '', end_date: '', dates_confirmed: false };

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

export const CYCLES_QUERY_KEY = ['admin-legislature', 'cycles'] as const;

/**
 * Legislative cycles (Documentation/ELECTION_OUTCOMES_PRD.md §5.0) — one term of
 * one legislative body: each Colorado General Assembly and each U.S. Congress.
 * Officeholder terms take their dates from these rows, so the list shows which
 * cycles are confirmed and which are still expected dates computed by rule. The
 * next two cycles of each body always exist; the server creates them as needed.
 */
const CyclesView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const cycles = useQuery({
    queryKey: CYCLES_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get('/admin/legislature/cycles', { params: { state: 'CO' } });
      return response.data.data as CyclesPayload;
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/legislature/cycles', {
        body_id: form.body_id,
        number: form.number.trim() === '' ? null : parseInt(form.number, 10),
        label: form.label.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        dates_confirmed: form.dates_confirmed,
      }),
    onSuccess: (response) => {
      toast.success('Cycle added');
      setModalOpen(false);
      queryClient.invalidateQueries({ queryKey: CYCLES_QUERY_KEY });
      const created = response.data?.data;
      if (created?.id) navigate(`/admin-next/legislature/cycles/${created.id}`);
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to add cycle')),
  });

  const bodies = cycles.data?.bodies ?? [];

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, body_id: bodies[0]?.id ?? '' });
    setModalOpen(true);
  };

  const selectedBody = bodies.find((body) => body.id === form.body_id);
  const numberMissing = Boolean(selectedBody?.numbered) && form.number.trim() === '';
  const saveDisabled =
    !form.body_id || numberMissing || !form.start_date || !form.end_date || form.end_date <= form.start_date;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          Each term of a legislature. Officeholder terms start and end on these dates.
        </p>
        <Button variant="primary" size="sm" icon={Plus} disabled={bodies.length === 0} onClick={openAdd}>
          Add Cycle
        </Button>
      </div>

      {cycles.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <WarningIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load legislative cycles.</p>
          <Button variant="outline" size="sm" onClick={() => cycles.refetch()}>Retry</Button>
        </div>
      ) : cycles.isLoading ? (
        <StandardTable>
          <StandardTableBody>
            <LoadingTableState colSpan={COLUMN_COUNT} />
          </StandardTableBody>
        </StandardTable>
      ) : (
        bodies.map((body) => {
          const rows = (cycles.data?.cycles ?? []).filter((cycle) => cycle.body_id === body.id);
          return (
            <section key={body.id} className="space-y-2">
              <SectionHeading note={body.level === 'federal' ? 'Federal' : body.state_code ?? undefined}>
                {body.name}
              </SectionHeading>
              <StandardTable horizontalScroll loading={cycles.isFetching && !cycles.isLoading}>
                <StandardTableHeader>
                  <StandardTableRow>
                    <StandardTableHeaderCell>Cycle</StandardTableHeaderCell>
                    <StandardTableHeaderCell columnType="nowrap">Dates</StandardTableHeaderCell>
                    <StandardTableHeaderCell responsiveHide="md">Sessions</StandardTableHeaderCell>
                    <StandardTableHeaderCell responsiveHide="md" columnType="nowrap">Milestones</StandardTableHeaderCell>
                  </StandardTableRow>
                </StandardTableHeader>
                <StandardTableBody>
                  {rows.length === 0 ? (
                    <EmptyTableState colSpan={COLUMN_COUNT} message="No cycles yet"
                      icon={<CalendarRange className="h-8 w-8" />} />
                  ) : (
                    rows.map((cycle) => (
                      <StandardTableRow key={cycle.id}
                        onClick={() => navigate(`/admin-next/legislature/cycles/${cycle.id}`)}>
                        <StandardTableCell>
                          <span className="font-medium text-text-primary dark:text-text-dark-primary">{cycle.label}</span>
                          <CycleChips cycle={cycle} />
                        </StandardTableCell>
                        <StandardTableCell>
                          <span className="whitespace-nowrap">
                            {formatDate(cycle.start_date)} – {formatDate(cycle.end_date)}
                          </span>
                        </StandardTableCell>
                        <StandardTableCell responsiveHide="md">
                          {cycle.sessions.length > 0
                            ? cycle.sessions.map((session) => session.short_label).join(', ')
                            : <span className="text-text-tertiary dark:text-text-dark-tertiary">—</span>}
                        </StandardTableCell>
                        <StandardTableCell responsiveHide="md">{cycle.milestone_count}</StandardTableCell>
                      </StandardTableRow>
                    ))
                  )}
                </StandardTableBody>
              </StandardTable>
            </section>
          );
        })
      )}

      <AdminModal
        isOpen={modalOpen}
        title="Add Cycle"
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={saveDisabled}
              loading={createMutation.isLoading} onClick={() => createMutation.mutate()}>
              Save Cycle
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Upcoming cycles are added automatically. Add one by hand only for a body or a period the
            automatic dates don't cover.
          </p>
          <div>
            <label htmlFor="cycle-body" className={labelClasses}>Legislative body</label>
            <select id="cycle-body" value={form.body_id} className="input"
              onChange={(event) => setForm((current) => ({ ...current, body_id: event.target.value }))}>
              {bodies.map((body) => (
                <option key={body.id} value={body.id}>{body.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="cycle-number" className={labelClasses}>Number{selectedBody?.numbered ? ' *' : ''}</label>
              <input id="cycle-number" type="number" min={1} value={form.number} className="input"
                placeholder="e.g. 78"
                onChange={(event) => setForm((current) => ({ ...current, number: event.target.value }))} />
            </div>
            <div>
              <label htmlFor="cycle-label" className={labelClasses}>Label</label>
              <input id="cycle-label" type="text" value={form.label} className="input" maxLength={100}
                placeholder={selectedBody?.numbered ? 'Defaults to e.g. “78th General Assembly”' : 'Required'}
                onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="cycle-start" className={labelClasses}>Start date *</label>
              <input id="cycle-start" type="date" value={form.start_date} className="input"
                onChange={(event) => setForm((current) => ({ ...current, start_date: event.target.value }))} />
            </div>
            <div>
              <label htmlFor="cycle-end" className={labelClasses}>End date *</label>
              <input id="cycle-end" type="date" value={form.end_date} className="input"
                onChange={(event) => setForm((current) => ({ ...current, end_date: event.target.value }))} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input id="cycle-confirmed" type="checkbox" checked={form.dates_confirmed}
              onChange={(event) => setForm((current) => ({ ...current, dates_confirmed: event.target.checked }))}
              className="h-4 w-4 rounded border-border-light-strong text-primary-600 focus:ring-primary-500 dark:border-border-dark" />
            <label htmlFor="cycle-confirmed" className="text-sm text-text-primary dark:text-text-dark-primary">
              Dates are confirmed
            </label>
          </div>
        </div>
      </AdminModal>
    </div>
  );
};

export default CyclesView;
